import React from 'react';
import AppLayout from '@/components/app/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Gift, Star, Trophy, Target, QrCode } from 'lucide-react';
const AppRewards = () => {
  // Mock data - in real app this would come from database
  const currentProgress = 680;
  const nextRewardThreshold = 1030;
  const remainingAmount = nextRewardThreshold - currentProgress;
  const progressPercentage = currentProgress / nextRewardThreshold * 100;
  const upcomingRewards = [{
    threshold: 1030,
    reward: "Free Botox Touch-up",
    description: "Complimentary 10-unit Botox refresh",
    icon: "💉"
  }, {
    threshold: 2000,
    reward: "VIP Experience Day",
    description: "Full spa day with premium treatments",
    icon: "👑"
  }, {
    threshold: 3500,
    reward: "Smile Makeover Credit",
    description: "$500 credit toward veneers or whitening",
    icon: "✨"
  }];
  const recentRewards = [{
    date: "Mar 15, 2024",
    reward: "2 Bonus Credits",
    reason: "Referral bonus for Sarah Johnson"
  }, {
    date: "Feb 28, 2024",
    reward: "Free Facial Add-on",
    reason: "Loyalty milestone reached"
  }];
  return <AppLayout>
      <div className="px-4 py-6 space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-juv-deep-chestnut mb-2">Rewards</h1>
          <p className="text-gray-600">Track your progress and unlock exclusive benefits</p>
        </div>

        {/* Progress Card */}
        <Card className="bg-gradient-to-r from-juv-terracotta to-juv-deep-chestnut text-white">
          <CardContent className="p-6">
            <div className="text-center mb-4">
              <Trophy className="w-8 h-8 mx-auto mb-2" />
              <h2 className="text-lg font-semibold mb-1">Almost there!</h2>
              <p className="text-white/80 text-sm">
                Only 2 more visits or spend ${remainingAmount.toFixed(2)} to unlock your next reward!
              </p>
            </div>
            
            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span>Current Progress</span>
                <span>${currentProgress}</span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-3">
                <div className="bg-white rounded-full h-3 transition-all duration-300" style={{
                width: `${progressPercentage}%`
              }}></div>
              </div>
              <div className="flex justify-between text-xs mt-1 text-white/70">
                <span>$0</span>
                <span>${nextRewardThreshold}</span>
              </div>
            </div>

            <Button variant="secondary" className="w-full">
              Check in for Rewards
            </Button>
          </CardContent>
        </Card>

        {/* Quick Check-in */}
        <Card>
          <CardContent className="p-4 bg-slate-50">
            <div className="flex items-center gap-4">
              <QrCode className="w-8 h-8 text-juv-terracotta" />
              <div className="flex-1">
                <h3 className="font-semibold text-juv-deep-chestnut">Quick Check-in</h3>
                <p className="text-sm text-gray-600">
                  Scan QR code at your appointment to earn progress points
                </p>
              </div>
              <Button variant="outline" size="sm">
                Scan QR
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Rewards */}
        <Card className="bg-slate-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-juv-deep-chestnut">
              <Gift className="w-5 h-5" />
              Upcoming Rewards
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {upcomingRewards.map((reward, index) => <div key={index} className="flex items-center gap-4 p-3 bg-juv-soft-blush rounded-lg">
                  <div className="text-2xl">{reward.icon}</div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-juv-deep-chestnut">{reward.reward}</h4>
                    <p className="text-sm text-gray-600 mb-1">{reward.description}</p>
                    <p className="text-xs text-juv-terracotta font-medium">
                      Unlock at ${reward.threshold} spent
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-juv-deep-chestnut">
                      ${reward.threshold - currentProgress} to go
                    </div>
                  </div>
                </div>)}
            </div>
          </CardContent>
        </Card>

        {/* Recent Rewards */}
        <Card className="bg-slate-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-juv-deep-chestnut">
              <Star className="w-5 h-5" />
              Recent Rewards
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentRewards.length > 0 ? <div className="space-y-3">
                {recentRewards.map((reward, index) => <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-juv-deep-chestnut">{reward.reward}</p>
                      <p className="text-sm text-gray-600">{reward.reason}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">{reward.date}</div>
                    </div>
                  </div>)}
              </div> : <div className="text-center py-8 text-gray-500">
                <Gift className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No rewards earned yet</p>
                <p className="text-sm">Complete treatments to start earning!</p>
              </div>}
          </CardContent>
        </Card>

        {/* Boost Progress CTA */}
        <Card className="bg-gradient-to-r from-juv-soft-blush to-juv-rose-clay">
          <CardContent className="p-6 text-center">
            <Target className="w-8 h-8 text-juv-terracotta mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-juv-deep-chestnut mb-2">
              Boost Your Progress
            </h3>
            <p className="text-gray-600 mb-4">
              Shop now to get closer to your next bonus reward
            </p>
            <Button className="bg-juv-terracotta hover:bg-juv-deep-chestnut">
              Shop Treatments
            </Button>
          </CardContent>
        </Card>

        {/* Referral Bonus */}
        <Card className="border-juv-rose-clay border-2">
          <CardContent className="p-4 bg-slate-50">
            <h3 className="font-semibold text-juv-deep-chestnut mb-2">Double Your Rewards</h3>
            <p className="text-sm text-gray-600 mb-3">
              Refer friends and earn 2 bonus credits + extra progress points for each successful referral.
            </p>
            <Button variant="outline" size="sm" className="border-juv-terracotta text-juv-terracotta">
              Refer Friends
            </Button>
          </CardContent>
        </Card>
      </div>
    </AppLayout>;
};
export default AppRewards;